export type BasicPermissions = {
    view: boolean;
    update: boolean;
};
export type NullPermission = {
    view: null;
    update: null;
};

export type NullablePermission = BasicPermissions | NullPermission;
export type TeamPermissions = BasicPermissions & {
    assign_owner: boolean;
};
export type AnyPermission = BasicPermissions | TeamPermissions | NullablePermission;

export type MakeDenied<T extends AnyPermission> = [T] extends [BasicPermissions | TeamPermissions]
    ? T & { [P in keyof T]: false }
    : NullPermission;

export type MakeReadOnly<T extends AnyPermission> = [T] extends [BasicPermissions | TeamPermissions]
    ? T & { [P in keyof T]: [P] extends ["view"] ? T[P] : false }
    : NullPermission;

export const nullPermission: NullPermission = {
    view: null,
    update: null,
};
export const basicDenyPermission: MakeDenied<BasicPermissions> = {
    view: false,
    update: false,
};
export const basicViewPermission: MakeReadOnly<BasicPermissions> = {
    view: true,
    update: false,
};
export const basicUpdatePermission: BasicPermissions = {
    view: true,
    update: true,
};
export const teamDenyPermission: MakeDenied<TeamPermissions> = {
    ...basicDenyPermission,
    assign_owner: false,
};
export const teamViewPermission: MakeReadOnly<TeamPermissions> = {
    ...teamDenyPermission,
    ...basicViewPermission,
};
export const teamUpdatePermission: TeamPermissions = {
    ...teamViewPermission,
    ...basicUpdatePermission,
};
export const teamOwnerPermission: TeamPermissions = {
    ...basicUpdatePermission,
    assign_owner: true,
};

export type MedicalPermissionSection =
    | "allergies"
    | "conditions"
    | "immunisations"
    | "medications"
    | "past_symptoms"
    | "supplements"
    | "surgeries"
    | "triggers";

export const EmptyMedical: Record<MedicalPermissionSection, NullPermission> = {
    allergies: nullPermission,
    conditions: nullPermission,
    immunisations: nullPermission,
    medications: nullPermission,
    past_symptoms: nullPermission,
    supplements: nullPermission,
    surgeries: nullPermission,
    triggers: nullPermission,
};

export const MedicalSections: MedicalPermissionSection[] = [
    "allergies",
    "conditions",
    "immunisations",
    "medications",
    "past_symptoms",
    "supplements",
    "surgeries",
    "triggers",
];

export const PermissionTitle: Record<Section, string> = {
    profile: "Profile",
    emergency: "Emergency",
    goals: "Goals",
    team: "Care team",
    photos: "Photographs",
    journal: "Other Journals",
    activities: "Activities",
    symptoms: "Symptoms",
    cycle: "Cycle",
    calendar: "Calendar",
    documents: "Documents",
    medical: "Medical",
    allergies: "Allergies",
    conditions: "Conditions",
    immunisations: "Immunisations",
    medications: "Medications",
    past_symptoms: "Past symptoms",
    supplements: "Supplements",
    surgeries: "Surgeries",
    triggers: "Triggers",
};
export const PermissionHint: Partial<Record<Section, string>> = {
    profile: "Contact details, basic medical information",
    emergency: "Emergency instructions and contacts",
    team: "Team members and invites",
    activities: "Activity journals",
    symptoms: "Symptom journals",
    journal: "Food, water, mood, sleep & other journals",
    cycle: "Periods and ovulation",
    calendar: "Appointments and events",
    documents: "Clinical documents, prescriptions, and pain plan",
    medical: "Health profile",
};

export interface ProfilePermissions {
    profile: BasicPermissions;
    goals: BasicPermissions;
    emergency: BasicPermissions;
    journal: BasicPermissions;
    activities: BasicPermissions;
    symptoms: BasicPermissions;
    cycle: BasicPermissions;
    calendar: BasicPermissions;
    documents: BasicPermissions;
    medical: BasicPermissions;
    allergies: NullablePermission;
    conditions: NullablePermission;
    immunisations: NullablePermission;
    medications: NullablePermission;
    past_symptoms: NullablePermission;
    supplements: NullablePermission;
    surgeries: NullablePermission;
    triggers: NullablePermission;
    team: TeamPermissions;
    photos: BasicPermissions;
}
export type ReadOnlyProfilePermissions = ProfilePermissions & {
    [P in keyof ProfilePermissions]: MakeReadOnly<ProfilePermissions[P]>;
};

export type PartialProfilePermissions<T extends ProfilePermissions = ProfilePermissions> = {
    [P in keyof T]?: Partial<T[P]> | null;
};

export type Section = string & keyof ProfilePermissions;
export type PermissionSection<S extends Section> = ProfilePermissions[S];
export type Ability<S extends Section> = string & keyof PermissionSection<S>;
export type Permission<S extends Section, A extends Ability<S>> = {
    section: S;
    ability: A;
};
export type ResolvedAbility<S extends Section, A extends Ability<S>> = boolean & (ProfilePermissions[S][A] | false);
export type PartialPermissionSection<S extends Section> = Partial<PermissionSection<S>>;
export type PermissionEntry<S extends Section> = [S, PermissionSection<S>];
export type PartialPermissionEntry<S extends Section> = [S, PartialPermissionSection<S>];
export type PermissionSectionEntry<S extends Section, A extends Ability<S> = Ability<S>> = [A, ResolvedAbility<S, A>];

export type PermissionCheck = <S extends Section, A extends Ability<S>>(ability: A | A[], section: S | S[]) => boolean;

export interface PermissionProvider {
    can: PermissionCheck;
    canAny: PermissionCheck;
    cannot: PermissionCheck;
    cannotAny: PermissionCheck;
}

export type PermissionProps = {
    can: PermissionCheck;
    canAny: PermissionCheck;
    cannot: PermissionCheck;
    cannotAny: PermissionCheck;
};
